import React from 'react';

/**
 * Animated per-service glyphs. Each one renders in a consistent dark-framed
 * panel (like a tiny product preview) at the top of a service card.
 * Pure SVG + CSS keyframes — no JS during animation.
 */

const Frame = ({ children, ariaLabel }: { children: React.ReactNode; ariaLabel: string }) => (
  <div className="service-glyph" role="img" aria-label={ariaLabel}>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// AI Voice Agents — handset + expanding sound-wave rings
// ---------------------------------------------------------------------------
export const VoiceGlyph = () => (
  <Frame ariaLabel="AI voice agent visualization">
    <svg viewBox="0 0 200 120" className="service-glyph__svg" preserveAspectRatio="xMidYMid meet">
      {/* Sound waves on each side of the handset */}
      {[0, 1, 2].map((i) => (
        <g key={i} className="voice-ring" style={{ animationDelay: `${i * 0.7}s` }}>
          <path d="M 70 60 Q 50 60 50 40 Q 50 20 70 20" fill="none" strokeLinecap="round" />
          <path d="M 130 60 Q 150 60 150 40 Q 150 20 130 20" fill="none" strokeLinecap="round" />
          <path d="M 70 60 Q 50 60 50 80 Q 50 100 70 100" fill="none" strokeLinecap="round" />
          <path d="M 130 60 Q 150 60 150 80 Q 150 100 130 100" fill="none" strokeLinecap="round" />
        </g>
      ))}
      {/* Handset body */}
      <rect x="86" y="42" width="28" height="38" rx="6" fill="var(--color-accent)" />
      <rect x="92" y="48" width="16" height="20" rx="2" fill="rgba(0,0,0,0.4)" />
      <circle cx="100" cy="74" r="1.5" fill="rgba(0,0,0,0.5)" />
    </svg>
  </Frame>
);

// ---------------------------------------------------------------------------
// Workflow Automation — 4-node diamond with a data packet orbiting
// ---------------------------------------------------------------------------
export const WorkflowGlyph = () => (
  <Frame ariaLabel="Workflow automation visualization">
    <svg viewBox="0 0 200 120" className="service-glyph__svg" preserveAspectRatio="xMidYMid meet">
      {/* Connection edges */}
      <path
        d="M 100 24 L 160 60 L 100 96 L 40 60 Z"
        fill="none"
        stroke="rgba(255,255,255,0.14)"
        strokeWidth="1.25"
        strokeDasharray="2 3"
      />
      {/* Nodes */}
      <g className="wf-nodes">
        <g>
          <circle cx="100" cy="24" r="11" fill="var(--color-surface-1)" stroke="var(--color-accent)" strokeWidth="1.5" />
          <circle cx="100" cy="24" r="3" fill="var(--color-accent)" />
        </g>
        <g>
          <circle cx="160" cy="60" r="11" fill="var(--color-surface-1)" stroke="var(--color-accent)" strokeWidth="1.5" />
          <circle cx="160" cy="60" r="3" fill="var(--color-accent)" />
        </g>
        <g>
          <circle cx="100" cy="96" r="11" fill="var(--color-surface-1)" stroke="var(--color-accent)" strokeWidth="1.5" />
          <circle cx="100" cy="96" r="3" fill="var(--color-accent)" />
        </g>
        <g>
          <circle cx="40" cy="60" r="11" fill="var(--color-surface-1)" stroke="var(--color-accent)" strokeWidth="1.5" />
          <circle cx="40" cy="60" r="3" fill="var(--color-accent)" />
        </g>
      </g>
      {/* Traveling packet */}
      <circle r="4" fill="var(--color-accent)" className="wf-packet">
        <animateMotion
          dur="4.2s"
          repeatCount="indefinite"
          path="M 100 24 L 160 60 L 100 96 L 40 60 Z"
          rotate="auto"
        />
      </circle>
      <circle r="7" fill="none" stroke="var(--color-accent)" strokeOpacity="0.5" className="wf-packet-halo">
        <animateMotion
          dur="4.2s"
          repeatCount="indefinite"
          path="M 100 24 L 160 60 L 100 96 L 40 60 Z"
        />
      </circle>
    </svg>
  </Frame>
);

// ---------------------------------------------------------------------------
// Custom AI Assistants — chat bubble with typing dots + token counter
// ---------------------------------------------------------------------------
export const ChatGlyph = () => (
  <Frame ariaLabel="AI assistant chat visualization">
    <svg viewBox="0 0 200 120" className="service-glyph__svg" preserveAspectRatio="xMidYMid meet">
      {/* User stub */}
      <rect
        x="92"
        y="18"
        width="80"
        height="22"
        rx="8"
        fill="rgba(255,255,255,0.04)"
        stroke="rgba(255,255,255,0.10)"
        strokeWidth="1"
      />
      <rect x="100" y="27" width="44" height="4" rx="2" fill="rgba(255,255,255,0.25)" />
      <rect x="148" y="27" width="16" height="4" rx="2" fill="rgba(255,255,255,0.15)" />

      {/* Assistant reply (typing) */}
      <rect
        x="28"
        y="52"
        width="92"
        height="28"
        rx="8"
        fill="rgba(198,242,78,0.08)"
        stroke="rgba(198,242,78,0.30)"
        strokeWidth="1"
      />
      <circle cx="46" cy="66" r="3" fill="var(--color-accent)" className="chat-dot" style={{ animationDelay: '0s' }} />
      <circle cx="62" cy="66" r="3" fill="var(--color-accent)" className="chat-dot" style={{ animationDelay: '0.18s' }} />
      <circle cx="78" cy="66" r="3" fill="var(--color-accent)" className="chat-dot" style={{ animationDelay: '0.36s' }} />

      {/* Meta token row */}
      <rect x="28" y="92" width="144" height="16" rx="4" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
      <text x="36" y="103" fill="var(--color-fg-muted)" fontSize="8" fontFamily="JetBrains Mono, monospace" letterSpacing="1.2">
        TOKENS
      </text>
      <text x="164" y="103" fill="var(--color-accent)" fontSize="9" fontFamily="JetBrains Mono, monospace" textAnchor="end" fontWeight="500">
        1,284
      </text>
    </svg>
  </Frame>
);

export const glyphFor = (id: string) => {
  if (id === 'ai-voice-agents') return <VoiceGlyph />;
  if (id === 'n8n-workflow-automation') return <WorkflowGlyph />;
  if (id === 'custom-llm-training') return <ChatGlyph />;
  return null;
};
